/**
 * services/api.js — Bar Experts
 * Camada centralizada de comunicação com a API Quart.
 */
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  timeout: 5000,
})

api.interceptors.request.use((config) => {
  const auth = useAuthStore()
  if (auth.token) config.headers.Authorization = `Bearer ${auth.token}`
  return config
})

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const auth = useAuthStore()
      auth.logout('/')
    }
    return Promise.reject(err)
  }
)

export const getArticle = (id) => api.get(`/articles/${id}`)
export const listArticles = (params = {}) => api.get('/articles', { params })
export const createArticle = (data) => api.post('/admin/articles', data)
export const updateArticle = (id, data) => api.put(`/admin/articles/${id}`, data)
export const getAdminArticle = (id) => api.get(`/admin/articles/${id}`)
export const listAdminArticles = (params = {}) => api.get('/admin/articles', { params })
export const submitForAudit = (id) => api.post(`/admin/articles/${id}/submit`)
export const approveArticle = (id, force = false) => api.post(`/admin/articles/${id}/approve`, { force })
export const rejectArticle = (id, note) => api.post(`/admin/articles/${id}/reject`, { note })
export const changeArticleStatus = (id, status, reason = '') => api.post(`/admin/articles/${id}/change-status`, { status, reason })
export const deleteArticle = (id) => api.delete(`/admin/articles/${id}`)
export const publishToInstagramApi = (id, config = {}) => api.post(`/admin/articles/${id}/publish-instagram`, config)
export const loginApi = (email, password) => api.post('/auth/login', { email, password })
export const getMe = () => api.get('/auth/me')
export const requestPasswordResetApi = (email) => api.post('/auth/forgot-password/request', { email })
export const verifyPasswordResetApi = (email, cocktail_name, new_password) => api.post('/auth/forgot-password/verify', { email, cocktail_name, new_password })
export const changePasswordApi = (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password })

// Consultor (Fase 5 - Estoque, Produções, Menus, Cocktails e TheCocktailDB)
export const listConsultorStock = (params = {}) => api.get('/consultor/stock', { params })
export const createConsultorStockItem = (data) => api.post('/consultor/stock', data)
export const updateConsultorStockItem = (id, data) => api.put(`/consultor/stock/${id}`, data)
export const deleteConsultorStockItem = (id) => api.delete(`/consultor/stock/${id}`)
export const getCaosStockCatalog = (params = {}) => api.get('/consultor/caos-stock', { params })
export const importFromCaosStock = (data) => api.post('/consultor/stock/import-from-caos', data)

export const listConsultorProductions = (params = {}) => api.get('/consultor/productions', { params })
export const createConsultorProduction = (data) => api.post('/consultor/productions', data)
export const updateConsultorProduction = (id, data) => api.put(`/consultor/productions/${id}`, data)
export const deleteConsultorProduction = (id) => api.delete(`/consultor/productions/${id}`)

export const listConsultorMenus = () => api.get('/consultor/menus')
export const createConsultorMenu = (data) => api.post('/consultor/menus', data)
export const getConsultorMenu = (id) => api.get(`/consultor/menus/${id}`)
export const updateConsultorMenu = (id, data) => api.put(`/consultor/menus/${id}`, data)
export const deleteConsultorMenu = (id) => api.delete(`/consultor/menus/${id}`)
export const addMenuSection = (menuId, data) => api.post(`/consultor/menus/${menuId}/sections`, data)
export const deleteMenuSection = (menuId, sectionId) => api.delete(`/consultor/menus/${menuId}/sections/${sectionId}`)

export const listCanonicalMenus = (source = 'all') => api.get('/consultor/canonical-menus', { params: { source } })
export const cloneCanonicalMenu = (id) => api.post(`/consultor/canonical-menus/${id}/clone`)
export const listIbaReferenceMenus = () => api.get('/consultor/canonical-menus', { params: { source: 'iba' } })
export const cloneIbaReferenceMenu = (id) => api.post(`/consultor/canonical-menus/${id}/clone`)
export const getMenuInventory = (menuId) => api.get(`/consultor/menus/${menuId}/inventory`)
export const recalculateMenuInventory = (menuId, data) => api.post(`/consultor/menus/${menuId}/recalculate-inventory`, data)
export const batchUpdateMenuPricing = (menuId, data) => api.put(`/consultor/menus/${menuId}/pricing`, data)

export const listConsultorCocktails = (params = {}) => api.get('/consultor/cocktails', { params })
export const createConsultorCocktail = (data) => api.post('/consultor/cocktails', data)
export const updateConsultorCocktail = (id, data) => api.put(`/consultor/cocktails/${id}`, data)
export const deleteConsultorCocktail = (id) => api.delete(`/consultor/cocktails/${id}`)

export const searchTheCocktailDB = (q) => api.get('/consultor/thecocktaildb/search', { params: { q } })
export const getRandomTheCocktailDB = () => api.get('/consultor/thecocktaildb/random')
export const getTheCocktailDBIngredients = () => api.get('/consultor/thecocktaildb/ingredients')

export const listConsultorSheets = () => api.get('/consultor/fichas-tecnicas')
export const createConsultorSheet = (data) => api.post('/consultor/fichas-tecnicas', data)
export const getConsultorSheet = (id) => api.get(`/consultor/fichas-tecnicas/${id}`)
export const generateConsultorZPL = (id) => api.get(`/consultor/fichas-tecnicas/${id}/zpl`)

// Menus Públicos
export const getPublicMenuApi = (slug, token = null) => {
  const headers = token ? { 'X-Menu-Token': token } : {}
  return api.get(`/menus/view/${slug}`, { headers })
}
// Visão técnica (custo, CMV, preço): só o consultor dono do cardápio ou o Super Admin
export const getOperationalMenuApi = (slug) => api.get(`/menus/view/${slug}/operational`)
export const unlockPublicMenuApi = (slug, password) => api.post(`/menus/view/${slug}/unlock`, { password })

// Biblioteca Pública & Obras
export const getLibraryItemsApi = (params = {}) => api.get('/library/items', { params })
export const getLibraryBookDetailApi = (slug) => api.get(`/library/books/${slug}`)
export const getAdminLibraryItemsApi = () => api.get('/library/admin/items')
export const toggleAdminLibraryItemApi = (data) => api.post('/library/admin/toggle', data)
export const getAdminMaterialsApi = () => api.get('/library/admin/materials')
export const createAdminMaterialApi = (data) => api.post('/library/admin/materials', data)
export const updateAdminMaterialApi = (id, data) => api.put(`/library/admin/materials/${id}`, data)
export const deleteAdminMaterialApi = (id) => api.delete(`/library/admin/materials/${id}`)
export const uploadMaterialPdfApi = (formData) => api.post('/library/admin/materials/upload-pdf', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
})
export const uploadMaterialCoverApi = (formData) => api.post('/library/admin/materials/upload-cover', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
})

// Coquetéis & Acervo Público
export const listCocktailsApi = (params = {}) => api.get('/cocktails', { params })
export const getCocktailCollectionsApi = () => api.get('/cocktails/collections')
export const getCocktailDetailApi = (identifier) => api.get(`/cocktails/${identifier}`)
export const getDailyCocktailApi = () => api.get('/cocktails/daily')

// Cursos
export const fetchCoursesApi = (params = {}) => api.get('/courses/', { params })
export const getCourseDetailApi = (slug, params = {}) => api.get(`/courses/${slug}`, { params })
export const createCourseApi = (data) => api.post('/courses/', data)
export const updateCourseApi = (slug, data) => api.put(`/courses/${slug}`, data)
export const deleteCourseApi = (slug) => api.delete(`/courses/${slug}`)

// Notificações
export const getNotificationsApi = () => api.get('/notifications/')
export const markNotificationAsReadApi = (id) => api.put(`/notifications/${id}/read`)
export const markAllNotificationsAsReadApi = () => api.put('/notifications/read-all')
export const deleteNotificationApi = (id) => api.delete(`/notifications/${id}`)
export const clearReadNotificationsApi = () => api.delete('/notifications/clear-read')

// Usuários (Busca e Perfil)
export const searchUsersApi = (nick) => api.get('/users/search', { params: { nick } })
export const updateProfileApi = (data) => api.put('/users/me', data)

// Newsletter & WhatsApp Hub (N4+)
export const getNewsletterSubscribers = (page = 1, search = '', waFilter = '') => {
  const params = { page, per_page: 20 }
  if (search) params.search = search
  if (waFilter !== '') params.whatsapp_added = waFilter
  return api.get('/newsletter/subscribers', { params }).then(r => r.data)
}
export const removeNewsletterSubscriber = (id) => api.delete(`/newsletter/subscribers/${id}`).then(r => r.data)
export const updateNewsletterSubscriber = (id, data) => api.put(`/newsletter/subscribers/${id}`, data).then(r => r.data)
export const addSubscriberToWhatsApp = (id) => api.post(`/newsletter/subscribers/${id}/add-to-whatsapp`).then(r => r.data)
export const broadcastWhatsAppMessage = (message, groupJid = null) => {
  const payload = { message }
  if (groupJid) payload.group_jid = groupJid
  return api.post('/newsletter/broadcast', payload).then(r => r.data)
}
export const getWhatsAppStatus = () => api.get('/newsletter/whatsapp/status').then(r => r.data)
export const getWhatsAppGroupMembers = (groupJid = null) => {
  const params = groupJid ? { group_jid: groupJid } : {}
  return api.get('/newsletter/whatsapp/group-members', { params }).then(r => r.data)
}


// Super Admin (Modo Deus N5)
export const getSuperAdminUsers = () => api.get('/superadmin/users')
export const createSuperAdminUser = (payload) => api.post('/superadmin/users', payload)
export const updateSuperAdminUser = (id, payload) => api.put(`/superadmin/users/${id}`, payload)
export const deleteSuperAdminUser = (id) => api.delete(`/superadmin/users/${id}`)

// Mídia Local (Uploads e Galeria)
export const uploadMediaApi = (formData, onProgress = null) => {
  return api.post('/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(percentCompleted)
      }
    }
  })
}
export const listMediaApi = (params = {}) => api.get('/media', { params })
export const deleteMediaApi = (id) => api.delete(`/media/${id}`)

// Biblioteca e Cocktails Públicos
export const getDailyCocktails = () => api.get('/library/daily-cocktails')
export const getCocktailDetail = (slugOrId) => api.get(`/library/cocktails/${slugOrId}`)

// 2º Campeonato Off Z Wagon (Inscrições & Mesa de Jurados)
export const listChampionshipEntries = (params = {}) => api.get('/championship/entries', { params })
export const scrapeChampionshipUrl = (data) => api.post('/championship/entries/scrape-url', data)
export const rescrapeChampionshipEntry = (id) => api.post(`/championship/entries/${id}/rescrape`)
export const triggerChampionshipHashtagScan = () => api.post('/championship/entries/scrape-hashtags')
export const updateChampionshipEntry = (id, data) => api.patch(`/championship/entries/${id}`, data)
export const deleteChampionshipEntry = (id) => api.delete(`/championship/entries/${id}`)
export const exportChampionshipEntriesCsv = () => api.get('/championship/entries/export', { responseType: 'blob' })

export default api

